import React, { useState, useEffect, useMemo } from 'react';
import { 
  Table, 
  Button, 
  Input, 
  Select, 
  Card, 
  Space, 
  Tag, 
  Typography, 
  Row, 
  Col,
  Alert,
  Spin,
  Tooltip,
  Popconfirm,
  message,
  Avatar,
  Statistic
} from 'antd';
import { 
  PlusOutlined, 
  EditOutlined, 
  DeleteOutlined, 
  EyeOutlined,
  FilterOutlined,
  UserOutlined,
  PhoneOutlined,
  MailOutlined,
  CalendarOutlined,
  HeartOutlined,
  CheckCircleOutlined,
  ExclamationCircleOutlined,
  ManOutlined,
  WomanOutlined,
  QuestionCircleOutlined
} from '@ant-design/icons';
import { usePatientsPaginated, useDeletePatient, useGraphQLError } from '../hooks/useGraphQL';
import { useTranslation } from '../hooks/useTranslation';
import PatientEditModal from '../components/Appointment/PatientEditModal';


const { Title, Text } = Typography;
const { Option } = Select;
const { Search } = Input;

interface Patient {
  id: string;
  first_name_en: string;
  last_name_en: string;
  first_name_zh_hk: string;
  last_name_zh_hk: string;
  email?: string;
  phone_number?: string;
  birthdate: string;
  gender: string;
  status: string;
  emergency_contact_name?: string;
  emergency_contact_phone?: string;
  avatar_url?: string;
  blood_type?: string;
  nationality?: string;
  identity_document_type?: string;
  identity_document_number?: string;
  language_preferences?: string[];
  allergies?: string[];
  chronic_conditions?: string[];
  medications?: string[];
  notes?: string;
  created_at: string;
  updated_at: string;
  app_user: {
    id: string;
    firstname: string;
    lastname: string;
    email: string;
    phone_number: string;
  };
}

const Patients: React.FC = () => {
  const { t } = useTranslation();
  const [loadPatientsPaginated, { data, loading, error, refetch }] = usePatientsPaginated({});
  const [deletePatient] = useDeletePatient();
  const { handleError } = useGraphQLError();

  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [genderFilter, setGenderFilter] = useState<string>('all');
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [editingPatient, setEditingPatient] = useState<Patient | null>(null);

  // Build pagination DTO
  const paginationDto = useMemo(() => ({
    page: currentPage,
    limit: pageSize,
    search: searchTerm || undefined,
    status: statusFilter === 'all' ? undefined : statusFilter
  }), [currentPage, pageSize, searchTerm, statusFilter]);

  // Load patients when pagination params change
  useEffect(() => {
    loadPatientsPaginated({ variables: { paginationDto } });
  }, [loadPatientsPaginated, paginationDto]);

  // Debug logging
  console.log('Patients Query Result:', { data, loading, error });

  const patients = data?.patientsPaginated?.patients || [];
  const paginationInfo = data?.patientsPaginated?.pagination;

  // Client-side gender filter (since backend doesn't support it yet)
  const filteredPatients = patients.filter((patient: Patient) => {
    const matchesGender = genderFilter === 'all' || patient.gender === genderFilter;
    return matchesGender;
  });

  // Reset to first page when search or filter changes
  const handleSearchChange = (value: string) => {
    setSearchTerm(value);
    setCurrentPage(1);
  };

  const handleStatusFilterChange = (value: string) => {
    setStatusFilter(value);
    setCurrentPage(1);
  };

  const handleGenderFilterChange = (value: string) => {
    setGenderFilter(value);
    setCurrentPage(1);
  };

  const handlePageChange = (page: number, size?: number) => {
    setCurrentPage(page);
    if (size) {
      setPageSize(size);
    }
  };

  const handlePatientSuccess = (patient: Patient) => {
    // The modal will handle the success message and form reset
    setShowCreateModal(false);
    setEditingPatient(null);
  };

  const handleDeletePatient = async (id: string) => {
    try {
      await deletePatient({
        variables: { id }
      });
      message.success(t('patients.patientDeleted'));
      // Reload patients after deletion
      loadPatientsPaginated({ variables: { paginationDto } });
    } catch (error) {
      console.error('Error deleting patient:', error);
      message.error(handleError(error));
    }
  };

  const getStatusTag = (status: string) => {
    const statusConfig = {
      active: { color: 'success', text: t('common.active'), icon: <CheckCircleOutlined /> },
      inactive: { color: 'warning', text: t('common.inactive'), icon: <ExclamationCircleOutlined /> }
    };
    const config = statusConfig[status as keyof typeof statusConfig] || { color: 'default', text: status, icon: null };
    return (
      <Tag color={config.color as any} icon={config.icon}>
        {config.text}
      </Tag>
    );
  };

  const getGenderTag = (gender: string) => {
    const genderConfig = {
      male: { color: 'blue', text: t('patients.male'), icon: <ManOutlined /> },
      female: { color: 'pink', text: t('patients.female'), icon: <WomanOutlined /> },
      other: { color: 'default', text: t('patients.other'), icon: <QuestionCircleOutlined /> }
    };
    const config = genderConfig[gender as keyof typeof genderConfig] || { color: 'default', text: gender, icon: null };
    return (
      <Tag color={config.color as any} icon={config.icon}>
        {config.text}
      </Tag>
    );
  };

  const calculateAge = (dateOfBirth: string) => {
    if (!dateOfBirth) return null;
    const today = new Date();
    const birthDate = new Date(dateOfBirth);
    let age = today.getFullYear() - birthDate.getFullYear();
    const monthDiff = today.getMonth() - birthDate.getMonth();
    if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
      age--;
    }
    return age;
  };

  const columns = [
    {
      title: t('patients.patientName'),
      key: 'patient',
      render: (patient: Patient) => (
        <div style={{ display: 'flex', alignItems: 'center' }}>
          <Avatar 
            size={40}
            style={{ backgroundColor: '#1890ff' }}
            icon={<UserOutlined />}
          />
          <div style={{ marginLeft: 12 }}>
            <div style={{ fontWeight: 500 }}>
              {patient.first_name_en} {patient.last_name_en}
            </div>
            <Text type="secondary" style={{ fontSize: '12px' }}>
              {patient.first_name_zh_hk} {patient.last_name_zh_hk}
            </Text>
          </div>
        </div>
      ),
    },
    {
      title: t('patients.contact'),
      key: 'contact',
      render: (patient: Patient) => (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
          {patient.phone_number && (
            <div style={{ display: 'flex', alignItems: 'center' }}>
              <PhoneOutlined style={{ marginRight: 4, color: '#8c8c8c' }} />
              <Text style={{ fontSize: '12px' }}>{patient.phone_number}</Text>
            </div>
          )}
          {patient.email && (
            <div style={{ display: 'flex', alignItems: 'center' }}>
              <MailOutlined style={{ marginRight: 4, color: '#8c8c8c' }} />
              <Text style={{ fontSize: '12px' }}>{patient.email}</Text>
            </div>
          )}
        </div>
      ),
    },
    {
      title: t('patients.ageGender'),
      key: 'ageGender',
      render: (patient: Patient) => (
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <Text style={{ fontSize: '12px' }}>{patient.birthdate ? `${calculateAge(patient.birthdate)} ${t('patients.years')}` : '-'}</Text>
          {getGenderTag(patient.gender)}
        </div>
      ),
    },
    {
      title: t('patients.emergencyContact'),
      key: 'emergency',
      render: (patient: Patient) => (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          {patient.emergency_contact_name && (
            <Text style={{ fontSize: '12px' }}>{patient.emergency_contact_name}</Text>
          )}
          {patient.emergency_contact_phone && (
            <Text type="secondary" style={{ fontSize: '11px' }}>{patient.emergency_contact_phone}</Text>
          )}
        </div>
      ),
    },
    {
      title: t('patients.medicalInfo'),
      key: 'medical',
      render: (patient: Patient) => (
        <div style={{ maxWidth: 200 }}>
          {patient.chronic_conditions && patient.chronic_conditions.length > 0 && (
            <div style={{ fontSize: '11px', marginBottom: 2 }}>
              <Text strong>{t('patients.conditions')}:</Text> {patient.chronic_conditions.slice(0, 2).join(', ')}
              {patient.chronic_conditions.length > 2 && ` +${patient.chronic_conditions.length - 2} ${t('patients.more')}`}
            </div>
          )}
          {patient.allergies && patient.allergies.length > 0 && (
            <div style={{ fontSize: '11px' }}>
              <Text type="secondary">{t('patients.allergies')}:</Text> {patient.allergies.slice(0, 2).join(', ')}
              {patient.allergies.length > 2 && ` +${patient.allergies.length - 2} ${t('patients.more')}`}
            </div>
          )}
        </div>
      ),
    },
    {
      title: t('common.status'),
      key: 'status',
      render: (patient: Patient) => getStatusTag(patient.status),
    },
    {
      title: t('common.actions'),
      key: 'actions',
      render: (patient: Patient) => (
        <Space>
          <Tooltip title={t('common.edit')}>
            <Button
              type="text"
              icon={<EditOutlined />}
              onClick={() => {
                setEditingPatient(patient);
              }}
            />
          </Tooltip>
          <Tooltip title={t('common.view')}>
            <Button
              type="text"
              icon={<EyeOutlined />}
            />
          </Tooltip>
          <Popconfirm
            title={t('patients.deleteConfirm')}
            onConfirm={() => handleDeletePatient(patient.id)}
            okText={t('common.yes')}
            cancelText={t('common.no')}
          >
            <Tooltip title={t('common.delete')}>
              <Button
                type="text"
                danger
                icon={<DeleteOutlined />}
              />
            </Tooltip>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  if (loading) {
    return (
      <div style={{ textAlign: 'center', padding: '50px' }}>
        <Spin size="large" />
        <div style={{ marginTop: 16 }}>{t('patients.loadingPatients')}</div>
      </div>
    );
  }

  if (error) {
    return (
      <div>
        <Row justify="space-between" align="middle" style={{ marginBottom: 24 }}>
          <Col>
            <Title level={2} style={{ margin: 0 }}>{t('patients.title')}</Title>
            <Text type="secondary">{t('patients.subtitle')}</Text>
          </Col>
        </Row>
        
        <Card>
          <Alert
            message={t('patients.errorLoadingPatients')}
            description={handleError(error)}
            type="error"
            showIcon
            action={
              <Button size="small" onClick={() => window.location.reload()}>
                {t('common.retry')}
              </Button>
            }
          />
        </Card>
      </div>
    );
  }

  // Note: These stats are calculated from current page data only
  // For accurate totals, you'd need separate queries or aggregate endpoints
  const activePatients = patients.filter((p: Patient) => p.status === 'active').length;
  const malePatients = patients.filter((p: Patient) => p.gender === 'male').length;
  const femalePatients = patients.filter((p: Patient) => p.gender === 'female').length;
  const avgAge = patients.length > 0 ? Math.round(patients.reduce((sum: number, p: Patient) => sum + (calculateAge(p.birthdate) || 0), 0) / patients.length) : 0;

  return (
    <div>
      {/* Loading State */}
      {loading && (
        <Card style={{ marginBottom: 16 }}>
          <div style={{ textAlign: 'center', padding: '40px 0' }}>
            <Spin size="large" />
            <div style={{ marginTop: 16 }}>
              <Text>{t('patients.loadingPatients')}</Text>
            </div>
          </div>
        </Card>
      )}

      {/* Error Display */}
      {error && (
        <Alert
          message={t('patients.errorLoadingPatients')}
          description={
            <div>
              <p><strong>{t('patients.errorDetails')}:</strong></p>
              <p>{handleError(error)}</p>
              <p><strong>{t('patients.debugInfo')}:</strong></p>
              <ul>
                <li>{t('patients.graphQLEndpoint')}: {process.env.REACT_APP_CORE_BACKEND_ENDPOINT || 'http://localhost:8210/graphql'}</li>
                <li>{t('patients.authToken')}: {localStorage.getItem('adminToken') ? t('patients.present') : t('patients.missing')}</li>
                <li>{t('patients.errorType')}: {(error as any).graphQLErrors ? t('patients.graphQLError') : (error as any).networkError ? t('patients.networkError') : t('patients.unknownError')}</li>
              </ul>
              <Button 
                type="primary" 
                onClick={() => window.location.reload()}
                style={{ marginTop: 8 }}
              >
                {t('common.retry')}
              </Button>
            </div>
          }
          type="error"
          showIcon
          style={{ marginBottom: 16 }}
        />
      )}

      {/* <Row justify="space-between" align="middle" style={{ marginBottom: 24 }}>
        <Col>
          <Title level={2} style={{ margin: 0 }}>{t('patients.title')}</Title>
          <Text type="secondary">{t('patients.subtitle')}</Text>
        </Col>
        <Col>
          <Button
            type="primary"
            icon={<PlusOutlined />}
            onClick={() => {
              setShowCreateModal(true);
            }}
          >
            {t('patients.addPatient')}
          </Button>
        </Col>
      </Row> */}

      {/* Stats Cards */}
      {/* <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
        <Col xs={24} sm={12} lg={6}>
          <Card>
            <Statistic
              title={t('patients.totalPatients')}
              value={patients.length}
              prefix={<UserOutlined style={{ color: '#1890ff' }} />}
              valueStyle={{ color: '#1890ff' }}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} lg={6}>
          <Card>
            <Statistic
              title={t('common.active')}
              value={activePatients}
              prefix={<CheckCircleOutlined style={{ color: '#52c41a' }} />}
              valueStyle={{ color: '#52c41a' }}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} lg={6}>
          <Card>
            <Statistic
              title={t('patients.maleFemale')}
              value={`${malePatients}/${femalePatients}`}
              prefix={<HeartOutlined style={{ color: '#722ed1' }} />}
              valueStyle={{ color: '#722ed1' }}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} lg={6}>
          <Card>
            <Statistic
              title={t('patients.avgAge')}
              value={avgAge}
              prefix={<CalendarOutlined style={{ color: '#fa8c16' }} />}
              valueStyle={{ color: '#fa8c16' }}
              suffix={t('patients.years')}
            />
          </Card>
        </Col>
      </Row> */}

      {/* Filters */}
      <Card style={{ marginBottom: 24 }}>
        <Row gutter={[16, 16]}>
          <Col xs={24} sm={12} md={6}>
            <Search
              placeholder={t('patients.searchPatients')}
              value={searchTerm}
              onChange={(e) => handleSearchChange(e.target.value)}
              onSearch={handleSearchChange}
              allowClear
            />
          </Col>
          
          <Col xs={24} sm={12} md={6}>
            <Select
              placeholder={t('common.status')}
              value={statusFilter}
              onChange={handleStatusFilterChange}
              style={{ width: '100%' }}
              allowClear
            >
              <Option value="all">{t('patients.allStatus')}</Option>
              <Option value="active">{t('common.active')}</Option>
              <Option value="inactive">{t('common.inactive')}</Option>
            </Select>
          </Col>
          
          <Col xs={24} sm={12} md={6}>
            <Select
              placeholder={t('patients.gender')}
              value={genderFilter}
              onChange={handleGenderFilterChange}
              style={{ width: '100%' }}
              allowClear
            >
              <Option value="all">{t('patients.allGenders')}</Option>
              <Option value="male">{t('patients.male')}</Option>
              <Option value="female">{t('patients.female')}</Option>
              <Option value="other">{t('patients.other')}</Option>
            </Select>
          </Col>
          
          <Col xs={24} sm={12} md={6}>
            <Button icon={<FilterOutlined />} style={{ width: '100%' }}>
              {t('common.export')}
            </Button>
          </Col>
        </Row>
      </Card>

      {/* Patients Table */}
      <Card>
        <Table
          columns={columns}
          dataSource={filteredPatients}
          rowKey="id"
          pagination={{
            current: currentPage,
            pageSize: pageSize,
            total: paginationInfo?.total || 0,
            showSizeChanger: true,
            showQuickJumper: true,
            showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} items`,
            onChange: handlePageChange,
            onShowSizeChange: handlePageChange,
          }}
          scroll={{ x: 1400 }}
          loading={loading}
        />
      </Card>

      {/* Create/Edit Modal */}
      <PatientEditModal
        visible={showCreateModal || !!editingPatient}
        patient={editingPatient}
        mode={showCreateModal ? 'create' : 'edit'}
        onCancel={() => {
          setShowCreateModal(false);
          setEditingPatient(null);
        }}
        onSuccess={handlePatientSuccess}
      />
    </div>
  );
};



export default Patients; 